import React, { useState } from 'react';
import {
  Box,
  Menu,
  MenuItem,
  CircularProgress,
  Typography,
  Chip,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { jobsApi } from '../services/api';

interface JobStatusBoxProps {
  jobId: number;
  currentStatus: string;
  onStatusChange?: (newStatus: string) => void;
  canEdit?: boolean;
}

const JobStatusBox: React.FC<JobStatusBoxProps> = ({
  jobId,
  currentStatus,
  onStatusChange,
  canEdit = true,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [updating, setUpdating] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(currentStatus);

  const statusOptions = [
    { value: 'open', label: 'Open', color: '#4caf50' },
    { value: 'paused', label: 'Paused', color: '#ff9800' },
    { value: 'closed', label: 'Closed', color: '#9e9e9e' },
    { value: 'filled', label: 'Filled', color: '#2196f3' },
  ];

  const getStatusColor = (status: string) => {
    const option = statusOptions.find(opt => opt.value === status);
    return option?.color || '#9e9e9e';
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (canEdit && !updating) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = async (newStatus: string) => {
    if (newStatus === selectedStatus) {
      handleClose();
      return;
    }

    setUpdating(true);
    try {
      await jobsApi.update(jobId.toString(), { status: newStatus as 'open' | 'paused' | 'closed' | 'filled' });
      setSelectedStatus(newStatus);
      if (onStatusChange) {
        onStatusChange(newStatus);
      }
    } catch (error) {
      console.error('Failed to update job status:', error);
      // Reset to current status on error
      setSelectedStatus(currentStatus);
    } finally {
      setUpdating(false);
      handleClose();
    }
  };

  return (
    <>
      <Box
        component="span"
        onClick={handleClick}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 32,
          px: 2,
          borderRadius: 2,
          fontSize: '0.8125rem',
          fontWeight: 500,
          backgroundColor: getStatusColor(selectedStatus),
          color: 'white',
          cursor: canEdit ? 'pointer' : 'default',
          transition: 'all 0.2s',
          ...(canEdit && {
            '&:hover': {
              opacity: 0.8,
              transform: 'scale(1.02)',
            },
          }),
        }}
      >
        {updating ? <CircularProgress size={16} sx={{ color: 'white' }} /> : selectedStatus}
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <MenuItem disabled sx={{ opacity: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Change status to:
          </Typography>
        </MenuItem>
        {statusOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleStatusChange(option.value)}
            selected={option.value === selectedStatus}
          >
            <Chip
              label={option.label}
              size="small"
              sx={{ 
                mr: 1,
                backgroundColor: option.color,
                color: 'white',
              }}
            />
            {option.value === selectedStatus && (
              <CheckCircle fontSize="small" color="primary" />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default JobStatusBox;